/**
 * Lista canonica das 27 UFs do Brasil (26 estados + DF).
 *
 * `sigla` casa com Pydantic Literal no backend (junta_comercial_sigla
 * e similares). `nome` e usado em selects e mensagens user-facing.
 */

export interface IUF {
  sigla: string;
  nome: string;
}

export const UFS: ReadonlyArray<IUF> = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
];

export const UF_SIGLAS: ReadonlyArray<string> = UFS.map(u => u.sigla);

// Options no formato esperado por SelectInputForm.
export const UF_SELECT_OPTIONS = UFS.map(u => ({
  value: u.sigla,
  label: `${u.sigla} — ${u.nome}`,
}));

// Options usando o NOME do estado como valor (compat com campo legacy
// `junta_comercial_estado` que e texto livre — a IA do Slice E recebe
// 'Goias' e nao 'GO'). Mantido pra transicao gradual.
export const UF_NOME_SELECT_OPTIONS = UFS.map(u => ({
  value: u.nome,
  label: u.nome,
}));

// Normaliza um nome de estado pro `nome` CANÔNICO das UFS (acento-insensível).
// Necessário porque a extração IA devolve inconsistente ('Goias' vs 'Goiás', o
// próprio prompt pede sem acento) mas o dropdown casa por value=nome ACENTUADO —
// sem isto o valor sem acento não seleciona nada e o campo "vem vazio". Também
// aceita a sigla ('GO') por robustez. Retorna '' pra vazio; devolve o original
// se não reconhecer (não inventa). 2026-07-21.
export const semAcento = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export const normalizarNomeUf = (valor: string | null | undefined): string => {
  if (!valor || !valor.trim()) return '';
  const alvo = semAcento(valor);
  const match = UFS.find(u => semAcento(u.nome) === alvo || u.sigla.toLowerCase() === alvo);
  return match ? match.nome : valor;
};
